import React, { useEffect, useState } from "react";
import AdminShell from "@/components/AdminShell";
import api from "@/lib/api";
import { Link } from "react-router-dom";
import {
  Users as UsersIcon,
  TrendingUp,
  UserPlus,
  RefreshCw,
  Activity as ActivityIcon,
  PoundSterling,
  Gauge,
  AlertTriangle,
} from "lucide-react";

export default function AdminDashboard() {
  const [kpis, setKpis] = useState(null);
  const [feeds, setFeeds] = useState(null);
  const [mrr, setMrr] = useState([]);

  useEffect(() => {
    document.title = "Admin · AnswerWatch";
    (async () => {
      const [k, f, m] = await Promise.all([
        api.get("/admin/kpis"),
        api.get("/admin/kpis/feeds"),
        api.get("/admin/kpis/mrr-trend"),
      ]);
      setKpis(k.data);
      setFeeds(f.data);
      setMrr(m.data);
    })();
  }, []);

  return (
    <AdminShell>
      <div className="mb-6">
        <div className="mono text-[10px] uppercase tracking-[0.22em] text-zinc-500">
          Admin overview
        </div>
        <h1 className="display mt-1 text-3xl font-black tracking-tight">
          Platform health
        </h1>
      </div>

      {/* KPI STRIP */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2" data-testid="admin-kpis">
        <Kpi
          icon={UsersIcon}
          label="Total users"
          value={kpis?.total_users ?? "—"}
          badge={planBreakdownLabel(kpis?.plan_breakdown)}
          testid="kpi-total-users"
        />
        <Kpi
          icon={PoundSterling}
          label="MRR"
          value={kpis ? `£${kpis.mrr_gbp.toLocaleString()}` : "—"}
          helper="Active paid subs"
          testid="kpi-mrr"
        />
        <Kpi
          icon={UserPlus}
          label="New signups"
          value={
            kpis
              ? `${kpis.signups_today} / ${kpis.signups_week} / ${kpis.signups_month}`
              : "—"
          }
          helper="Today / 7d / 30d"
          testid="kpi-signups"
        />
        <Kpi
          icon={TrendingUp}
          label="Trial → Paid (30d)"
          value={kpis ? `${kpis.trial_to_paid_pct}%` : "—"}
          testid="kpi-conversion"
        />
        <Kpi
          icon={RefreshCw}
          label="Churn (30d)"
          value={kpis?.churn_count_30d ?? "—"}
          testid="kpi-churn"
        />
        <Kpi
          icon={ActivityIcon}
          label="Active users (7d)"
          value={kpis?.active_users_7d ?? "—"}
          testid="kpi-active"
        />
        <Kpi
          icon={PoundSterling}
          label="LLM spend"
          value={
            kpis
              ? `£${kpis.llm_spend_week_gbp} / £${kpis.llm_spend_month_gbp}`
              : "—"
          }
          helper="7d / MTD"
          testid="kpi-llm-spend"
        />
        <Kpi
          icon={Gauge}
          label="Margin ratio"
          value={kpis?.margin_ratio ? `${kpis.margin_ratio}x` : "—"}
          helper={kpis?.margin_flag_red ? "⚠ below 3x" : "MRR ÷ LLM cost"}
          testid="kpi-margin"
          danger={kpis?.margin_flag_red}
        />
      </div>

      {/* FEEDS */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 mt-6">
        <FeedCol
          title="Recent signups"
          testid="feed-signups"
          items={(feeds?.recent_signups || []).map((u) => ({
            key: u.user_id,
            primary: u.email,
            secondary: `${u.plan || "trial"} · ${shortDate(u.created_at)}`,
            href: `/admin/users/${u.user_id}`,
          }))}
        />
        <FeedCol
          title="Recent plan changes"
          testid="feed-plan-changes"
          items={(feeds?.recent_plan_changes || []).map((e) => ({
            key: e.activity_id,
            primary: e.description,
            secondary: shortDate(e.created_at),
            href: e.user_id ? `/admin/users/${e.user_id}` : null,
          }))}
        />
        <FeedCol
          title="Recent admin actions"
          testid="feed-admin-actions"
          items={(feeds?.recent_admin_actions || []).map((e) => ({
            key: e.audit_id,
            primary: `${e.action_type} · ${e.reason}`,
            secondary: shortDate(e.created_at),
            href: e.target_user_id ? `/admin/users/${e.target_user_id}` : null,
          }))}
        />
      </div>

      {/* MRR CHART */}
      <section className="mt-6 bg-zinc-900/60 border border-zinc-800 rounded-[4px] p-4" data-testid="mrr-chart">
        <div className="flex items-center justify-between">
          <div>
            <div className="mono text-[10px] uppercase tracking-[0.22em] text-zinc-500">
              MRR growth · 12 weeks
            </div>
            <div className="display mt-1 text-lg font-bold">
              £{mrr[mrr.length - 1]?.total?.toLocaleString() || 0}
            </div>
          </div>
          <div className="flex items-center gap-3 text-[10px] mono uppercase tracking-[0.16em]">
            <Swatch color="bg-emerald-400" label="Starter" />
            <Swatch color="bg-sky-400" label="Growth" />
            <Swatch color="bg-amber-400" label="Scale" />
          </div>
        </div>
        <MrrBars points={mrr} />
      </section>
    </AdminShell>
  );
}

function Kpi({ icon: Icon, label, value, helper, badge, testid, danger }) {
  return (
    <div
      data-testid={testid}
      className={`bg-zinc-900/60 border rounded-[4px] p-3 ${
        danger ? "border-rose-700" : "border-zinc-800"
      }`}
    >
      <div className="flex items-center justify-between">
        <div className="mono text-[10px] uppercase tracking-[0.22em] text-zinc-500 flex items-center gap-1">
          {Icon && <Icon size={11} />}
          {label}
        </div>
        {danger && <AlertTriangle size={12} className="text-rose-400" />}
      </div>
      <div className={`display mt-1.5 text-xl font-black tabular-nums ${danger ? "text-rose-300" : "text-white"}`}>
        {value}
      </div>
      {helper && <div className="text-[10px] text-zinc-500 mt-0.5">{helper}</div>}
      {badge && <div className="text-[10px] text-zinc-400 mt-0.5">{badge}</div>}
    </div>
  );
}

function planBreakdownLabel(pb) {
  if (!pb) return "";
  return Object.entries(pb)
    .map(([k, v]) => `${v} ${k}`)
    .join(" · ");
}

function FeedCol({ title, items, testid }) {
  return (
    <div className="bg-zinc-900/60 border border-zinc-800 rounded-[4px]" data-testid={testid}>
      <div className="px-3 py-2 border-b border-zinc-800 mono text-[10px] uppercase tracking-[0.22em] text-zinc-500">
        {title}
      </div>
      <div className="divide-y divide-zinc-800 max-h-[340px] overflow-auto">
        {items.length === 0 && (
          <div className="p-3 text-xs text-zinc-500">No recent entries.</div>
        )}
        {items.map((it) =>
          it.href ? (
            <Link
              key={it.key}
              to={it.href}
              className="block px-3 py-2 hover:bg-zinc-900 text-xs"
            >
              <div className="text-zinc-100 truncate">{it.primary}</div>
              <div className="mono text-[10px] uppercase tracking-[0.16em] text-zinc-500 mt-0.5">
                {it.secondary}
              </div>
            </Link>
          ) : (
            <div key={it.key} className="px-3 py-2 text-xs">
              <div className="text-zinc-100 truncate">{it.primary}</div>
              <div className="mono text-[10px] uppercase tracking-[0.16em] text-zinc-500 mt-0.5">
                {it.secondary}
              </div>
            </div>
          )
        )}
      </div>
    </div>
  );
}

function MrrBars({ points }) {
  if (!points.length) return <div className="h-20" />;
  const max = Math.max(1, ...points.map((p) => p.total));
  return (
    <div className="mt-4 h-32 flex items-end gap-1.5">
      {points.map((p, i) => (
        <div key={i} className="flex-1 flex flex-col justify-end" title={`£${p.total}`}>
          <div
            className="bg-amber-400"
            style={{ height: `${(p.scale / max) * 100}%` }}
          />
          <div
            className="bg-sky-400"
            style={{ height: `${(p.growth / max) * 100}%` }}
          />
          <div
            className="bg-emerald-400"
            style={{ height: `${(p.starter / max) * 100}%` }}
          />
        </div>
      ))}
    </div>
  );
}

function Swatch({ color, label }) {
  return (
    <span className="inline-flex items-center gap-1 text-zinc-400">
      <span className={`w-2 h-2 ${color}`} />
      {label}
    </span>
  );
}

function shortDate(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}
